import React from 'react';

import type { PopoverPosition } from '@mui/material';
import { Divider, Menu } from '@mui/material';
import { equals } from 'ramda';
import { Queries } from 'src/editor/queries';
import {
  CutMenuItem,
  DeleteMenuItem,
  DeleteTableColumnsMenuItem,
  DeleteTableMenuItem,
  DeleteTableRowsMenuItem,
  EditCopyMenuItem,
  InsertParagraphMenuItem,
  InsertTableColumnMenuItem,
  InsertTableRowsMenuItem,
  PasteMenuItem,
  RemoveParagraphMenuItem,
} from 'src/editor/toolbars';
import { useEditorLoading, useSlateSelector } from 'src/layout-engine';
import { useEditingDisabled } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

import { FitTableToContainerMenuItem } from './FitTableToContainerMenuItem';
import {
  ContinueListParagraphMenuItem,
  RestartListParagraphMenuItem,
} from './list-paragraph-menu-items';
import { OpenTablePropertiesMenuItem } from './OpenTablePropertiesMenuItem';
import { UpdateCrossReferencesMenuItem } from './UpdateCrossReferencesMenuItem';

const useStyles = makeStyles()(theme => ({
  divider: {
    margin: theme.spacing(1),
  },
}));

export type ContextMenuLocation = PopoverPosition | null;

interface DocumentContextMenu {
  location: ContextMenuLocation;
  setLocation: (location: ContextMenuLocation) => void;
}

// TODO: If changing, consider migrating to DeclarativeMenu
export function DocumentContextMenu({
  location,
  setLocation,
}: DocumentContextMenu): JSX.Element | null {
  const disabled = useEditingDisabled();

  function handleClose(): void {
    // `setTimeout` to give renderModel time to finish updating before re-rendering.
    setTimeout(() => setLocation(null));
  }

  return (
    <Menu
      anchorPosition={location !== null ? location : undefined}
      anchorReference="anchorPosition"
      open={location !== null}
      slotProps={{
        backdrop: {
          invisible: true,
          onContextMenu: (event: React.MouseEvent): void => {
            event.preventDefault();
            handleClose();
          },
        },
      }}
      onClose={handleClose}
    >
      <CutMenuItem closeRootMenu={handleClose} disabled={disabled} />
      <EditCopyMenuItem closeRootMenu={handleClose} />
      <PasteMenuItem closeRootMenu={handleClose} disabled={disabled} />
      <DeleteMenuItem closeRootMenu={handleClose} disabled={disabled} />
      <ContinueListParagraphMenuItem closeRootMenu={handleClose} disabled={disabled} />
      <RestartListParagraphMenuItem closeRootMenu={handleClose} disabled={disabled} />
      <UpdateCrossReferencesMenuItem closeRootMenu={handleClose} disabled={disabled} />
      <RemoveParagraphMenuItem closeRootMenu={handleClose} disabled={disabled} />
      <InsertParagraphMenuItem closeRootMenu={handleClose} direction="above" disabled={disabled} />
      <InsertParagraphMenuItem closeRootMenu={handleClose} direction="below" disabled={disabled} />
      <TableMenuItems handleClose={handleClose} />
    </Menu>
  );
}

interface TableMenuItemsProps {
  handleClose: () => void;
}

function TableMenuItems({ handleClose }: TableMenuItemsProps): JSX.Element | null {
  const { classes } = useStyles();
  const editorLoading = useEditorLoading();
  const { cellEntries, isNestedTable, isSelectionWithinTable, rowEntries } = useSlateSelector(
    editor => {
      if (!editor.selection)
        return {
          cellEntries: [],
          isNestedTable: false,
          isSelectionWithinTable: false,
          rowEntries: [],
        };

      const cellEntries = Queries.getContiguousTableCells(editor);
      const { rowEntries } = Queries.getContiguousTableRows(editor);
      const { isNestedTable, isSelectionWithinTable } = Queries.getIsSelectionWithinTable(editor);

      return { cellEntries, isNestedTable, isSelectionWithinTable, rowEntries };
    },
    equals
  );

  if (!isSelectionWithinTable) return null;

  return (
    <div>
      <Divider className={classes.divider} />
      <InsertTableRowsMenuItem
        closeRootMenu={handleClose}
        disabled={editorLoading}
        numberOfRows={rowEntries.length}
        rowLocation="above"
      />
      <InsertTableRowsMenuItem
        closeRootMenu={handleClose}
        disabled={editorLoading}
        numberOfRows={rowEntries.length}
        rowLocation="below"
      />
      <InsertTableColumnMenuItem
        closeRootMenu={handleClose}
        columnLocation="left"
        disabled={editorLoading}
        numberOfColumns={cellEntries.length}
      />
      <InsertTableColumnMenuItem
        closeRootMenu={handleClose}
        columnLocation="right"
        disabled={editorLoading}
        numberOfColumns={cellEntries.length}
      />
      <Divider className={classes.divider} />
      <DeleteTableRowsMenuItem
        closeRootMenu={handleClose}
        disabled={editorLoading}
        numberOfRows={rowEntries.length}
      />
      <DeleteTableColumnsMenuItem
        closeRootMenu={handleClose}
        disabled={editorLoading}
        numberOfColumns={cellEntries.length}
      />
      <DeleteTableMenuItem closeRootMenu={handleClose} disabled={editorLoading} />
      <Divider className={classes.divider} />
      <FitTableToContainerMenuItem
        closeRootMenu={handleClose}
        disabled={editorLoading}
        isNestedTable={isNestedTable}
      />
      <OpenTablePropertiesMenuItem closeRootMenu={handleClose} disabled={editorLoading} />
    </div>
  );
}
